import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "@/lib/axios";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Music, Search, Plus, Trash2, RefreshCw, Play, Pause, Upload, Save, Sparkles } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface MusicTrack {
  _id?: string;
  id?: string;
  name?: string;
  title?: string;
  originalName?: string;
  fileName?: string;
  url?: string;
  cdnUrl?: string;
  duration?: number;
  tags?: string[];
  category?: string;
  volumeLevel?: number;
  volume?: number;
  fadeInDuration?: number;
  fadeOutDuration?: number;
  fadeIn?: number;
  fadeOut?: number;
  createdAt?: string;
  artist?: string;
  album?: string;
}

const MusicCollectionManager = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("music-library");
  const [playingTrack, setPlayingTrack] = useState<string | null>(null);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Dosya yükleme için state'ler
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFormOpen, setUploadFormOpen] = useState(false);
  const [uploadData, setUploadData] = useState({
    title: "",
    category: "",
    file: null as File | null
  });

  // Efekt yükleme için state'ler
  const [effectUploadFormOpen, setEffectUploadFormOpen] = useState(false);
  const [effectUploadData, setEffectUploadData] = useState({
    title: "",
    isIntro: true,
    isOutro: false,
    file: null as File | null
  });
  const [effectSearchQuery, setEffectSearchQuery] = useState("");

  // Müzik kütüphanesini getir
  const { data: musicTracks, isLoading } = useQuery({
    queryKey: ["music-tracks"],
    queryFn: async () => {
      try {
        const { data } = await axios.get("/ai-announcements/music");
        return data;
      } catch (error) {
        console.error("Müzik parçalarını getirme hatası:", error);
        throw error;
      }
    }
  });

  // Müzik yükleme işlemi
  const uploadTrack = useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await axios.post("/ai-announcements/music", formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["music-tracks"] });
      setUploadFormOpen(false);
      setUploadData({ title: "", category: "", file: null });
      toast({
        title: "Müzik yüklendi",
        description: "Müzik parçası başarıyla yüklendi."
      });
    },
    onError: (error) => {
      console.error("Yükleme hatası:", error);
      toast({
        title: "Hata",
        description: "Müzik yüklenirken bir hata oluştu.",
        variant: "destructive"
      });
    }
  });
  
  // Müzik dosyası seçme
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadData(prev => ({ ...prev, file: e.target.files![0] }));
    }
  };
  
  // Müzik yükleme
  const handleUpload = () => {
    if (!uploadData.title || !uploadData.category || !uploadData.file) {
      toast({
        title: "Eksik bilgi",
        description: "Lütfen tüm alanları doldurun.",
        variant: "destructive"
      });
      return;
    }
    
    setIsUploading(true);
    const formData = new FormData();
    formData.append("title", uploadData.title);
    formData.append("category", uploadData.category);
    formData.append("file", uploadData.file);
    
    uploadTrack.mutate(formData);
  };
  
  // Müzik silme işlemi
  const deleteTrack = useMutation({
    mutationFn: async (trackId: string) => {
      await axios.delete(`/ai-announcements/music/${trackId}`);
      return trackId;
    },
    onSuccess: (deletedId) => {
      toast({
        title: "Müzik silindi",
        description: "Müzik parçası başarıyla silindi."
      });
      queryClient.invalidateQueries({ queryKey: ["music-tracks"] });
      
      // Eğer çalınan parça silinmişse oynatmayı durdur
      if (playingTrack === deletedId) {
        setPlayingTrack(null);
      }
    },
    onError: () => {
      toast({
        title: "Hata",
        description: "Müzik silinirken bir hata oluştu.",
        variant: "destructive"
      });
    }
  });

  // Müzik çalma/duraklatma kontrolü
  const togglePlayTrack = (trackId: string, cdnUrl: string | undefined) => {
    if (!trackId || !cdnUrl) return; // Gerekli bilgiler yoksa işlem yapma
    
    if (playingTrack === trackId && audioElement) {
      // Şu an çalan parçayı durdur
      audioElement.pause();
      setPlayingTrack(null);
      setAudioElement(null);
    } else {
      // Mevcut sesi durdur (varsa)
      if (audioElement) {
        audioElement.pause();
      }
      
      // Yeni parçayı çal
      const audio = new Audio(cdnUrl);
      audio.addEventListener('ended', () => {
        setPlayingTrack(null);
        setAudioElement(null);
      });
      
      audio.play()
        .then(() => {
          console.log('Müzik çalınıyor:', cdnUrl);
        })
        .catch(error => {
          console.error('Müzik çalma hatası:', error);
          toast({
            title: "Hata",
            description: "Müzik çalınamadı. Lütfen daha sonra tekrar deneyin.",
            variant: "destructive"
          });
          setPlayingTrack(null);
        });
        
      setAudioElement(audio);
      setPlayingTrack(trackId);
    }
  };

  // Backend verisi ile frontend arasındaki farklılıklar için konsola yazdır
  useEffect(() => {
    if (musicTracks && musicTracks.length > 0) {
      console.log("Backend'den gelen ilk müzik parçası örneği:", musicTracks[0]);
    }
  }, [musicTracks]);

  // Müzik filtrelemesi (jingle'ları hariç tut)
  const filteredTracks = musicTracks?.filter((track: MusicTrack) => {
    // Jingle kategorilerini hariç tut
    if (track.category === 'intro_jingle' || track.category === 'outro_jingle') {
      return false;
    }

    // Arama sorgusu boşsa hepsini göster
    if (!searchQuery) return true;

    // Küçük harfe çevrilmiş arama sorgusu
    const query = searchQuery.toLowerCase();

    // Başlık kontrolü (name veya title alanında)
    const titleMatch =
      (track.name ? track.name.toLowerCase().includes(query) : false) ||
      (track.title ? track.title.toLowerCase().includes(query) : false);

    // Etiket kontrolü
    const tagMatch = track.tags && Array.isArray(track.tags)
      ? track.tags.some(tag => tag && tag.toLowerCase().includes(query))
      : false;

    // Kategori kontrolü
    const categoryMatch = track.category
      ? track.category.toLowerCase().includes(query)
      : false;

    // Sanatçı kontrolü
    const artistMatch = track.artist
      ? track.artist.toLowerCase().includes(query)
      : false;

    return titleMatch || tagMatch || categoryMatch || artistMatch;
  }) || [];

  // Efekt filtrelemesi (sadece jingle'ları göster)
  const filteredEffects = musicTracks?.filter((track: MusicTrack) => {
    // Sadece jingle kategorilerini göster
    if (track.category !== 'intro_jingle' && track.category !== 'outro_jingle') {
      return false;
    }

    // Arama sorgusu boşsa hepsini göster
    if (!effectSearchQuery) return true;

    const query = effectSearchQuery.toLowerCase();

    const titleMatch =
      (track.name ? track.name.toLowerCase().includes(query) : false) ||
      (track.title ? track.title.toLowerCase().includes(query) : false);

    return titleMatch;
  }) || [];

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Müzik Koleksiyonu</h1>
          <p className="text-muted-foreground">
            AI anons sisteminde kullanılacak arka plan müziklerini yönetin
          </p>
        </div>
        <Dialog open={uploadFormOpen} onOpenChange={setUploadFormOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Yeni Müzik Ekle
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Müzik Yükle</DialogTitle>
              <DialogDescription>
                AI anons sistemi için yeni bir arka plan müziği yükleyin
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Başlık</Label>
                <Input 
                  id="title" 
                  placeholder="Müzik başlığını girin" 
                  value={uploadData.title}
                  onChange={(e) => setUploadData(prev => ({ ...prev, title: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Kategori</Label>
                <select
                  id="category"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  value={uploadData.category}
                  onChange={(e) => setUploadData(prev => ({ ...prev, category: e.target.value }))}
                >
                  <option value="">Kategori seçin</option>
                  <option value="energetic">Enerjik</option>
                  <option value="calm">Sakin</option>
                  <option value="happy">Neşeli</option>
                  <option value="sad">Hüzünlü</option>
                  <option value="motivational">Motive Edici</option>
                  <option value="dramatic">Dramatik</option>
                  <option value="other">Diğer</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="file">Dosya</Label>
                <div className="border rounded-md p-4 text-center">
                  <Upload className="w-6 h-6 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-2">
                    MP3 dosyası sürükleyin veya seçin
                  </p>
                  <Input 
                    id="file" 
                    type="file" 
                    accept="audio/mp3, audio/mpeg" 
                    className="hidden" 
                    onChange={handleFileChange}
                  />
                  <p className="text-sm mb-2">
                    {uploadData.file ? `Seçilen dosya: ${uploadData.file.name}` : 'Dosya seçilmedi'}
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => document.getElementById('file')?.click()}
                    type="button"
                  >
                    Dosya Seç
                  </Button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setUploadFormOpen(false)}
                disabled={isUploading}
              >
                İptal
              </Button>
              <Button 
                onClick={handleUpload} 
                disabled={isUploading}
              >
                {isUploading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Yükleniyor...
                  </>
                ) : (
                  'Yükle'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="music-library" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="music-library">
            <Music className="w-4 h-4 mr-2" />
            Müzik Kütüphanesi
          </TabsTrigger>
          <TabsTrigger value="effects-library">
            <Sparkles className="w-4 h-4 mr-2" />
            Efekt Kütüphanesi
          </TabsTrigger>
          <TabsTrigger value="audio-settings">
            <Slider className="w-4 h-4 mr-2" />
            Ses Ayarları
          </TabsTrigger>
        </TabsList>

        <TabsContent value="music-library">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Müzik ara..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Müzik Kütüphanesi</CardTitle>
              <CardDescription>
                AI anons arka planı için kullanılabilir müzik parçaları
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : (
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Şarkı Adı</TableHead>
                        <TableHead>Sanatçı</TableHead>
                        <TableHead>Kategori</TableHead>
                        <TableHead>Süre</TableHead>
                        <TableHead>Önizleme</TableHead>
                        <TableHead className="text-right">İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTracks.length > 0 ? (
                        filteredTracks.map((track: MusicTrack) => (
                          <TableRow key={track._id || track.id}>
                            <TableCell className="font-medium">{track.originalName || 'İsimsiz Parça'}</TableCell>
                            <TableCell>{track.artist || 'Bilinmeyen Sanatçı'}</TableCell>
                            <TableCell>{track.category || 'Diğer'}</TableCell>
                            <TableCell>
                              {track.duration 
                                ? `${Math.floor(track.duration / 60)}:${(track.duration % 60).toString().padStart(2, '0')}` 
                                : '0:00'}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => togglePlayTrack(track._id || track.id || '', track.cdnUrl || track.url)}
                                disabled={!track.cdnUrl && !track.url}
                              >
                                {playingTrack === (track._id || track.id) ? (
                                  <>
                                    <Pause className="h-4 w-4 mr-1" />
                                    Durdur
                                  </>
                                ) : (
                                  <>
                                    <Play className="h-4 w-4 mr-1" />
                                    Dinle
                                  </>
                                )}
                              </Button>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const trackId = track._id || track.id;
                                  if (trackId) {
                                    deleteTrack.mutate(trackId);
                                  }
                                }}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-4">
                            {searchQuery ? (
                              <>
                                <p>Arama sonucunda müzik bulunamadı</p>
                                <Button
                                  variant="link"
                                  onClick={() => setSearchQuery("")}
                                >
                                  Filtreyi temizle
                                </Button>
                              </>
                            ) : (
                              <p>Henüz müzik kütüphanesinde parça bulunmuyor</p>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="effects-library">
          <div className="flex justify-between items-center mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Efekt ara..."
                className="pl-8"
                value={effectSearchQuery}
                onChange={(e) => setEffectSearchQuery(e.target.value)}
              />
            </div>
            <Dialog open={effectUploadFormOpen} onOpenChange={setEffectUploadFormOpen}>
              <DialogTrigger asChild>
                <Button className="ml-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Yeni Efekt Ekle
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Efekt Yükle</DialogTitle>
                  <DialogDescription>
                    Anons başı ve/veya sonu için efekt yükleyin
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="effect-title">Başlık</Label>
                    <Input
                      id="effect-title"
                      placeholder="Efekt başlığını girin"
                      value={effectUploadData.title}
                      onChange={(e) => setEffectUploadData(prev => ({ ...prev, title: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Kullanım Alanı</Label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={effectUploadData.isIntro}
                          onChange={(e) => setEffectUploadData(prev => ({ ...prev, isIntro: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm">Giriş Efekti</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={effectUploadData.isOutro}
                          onChange={(e) => setEffectUploadData(prev => ({ ...prev, isOutro: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm">Çıkış Efekti</span>
                      </label>
                    </div>
                    <p className="text-xs text-muted-foreground">İkisini de seçerseniz aynı efekt hem giriş hem çıkış olarak kullanılabilir</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="effect-file">Dosya</Label>
                    <div className="border rounded-md p-4 text-center">
                      <Upload className="w-6 h-6 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground mb-2">
                        MP3 dosyası sürükleyin veya seçin
                      </p>
                      <Input
                        id="effect-file"
                        type="file"
                        accept="audio/mp3, audio/mpeg"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setEffectUploadData(prev => ({ ...prev, file: e.target.files![0] }));
                          }
                        }}
                      />
                      <p className="text-sm mb-2">
                        {effectUploadData.file ? `Seçilen dosya: ${effectUploadData.file.name}` : 'Dosya seçilmedi'}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('effect-file')?.click()}
                        type="button"
                      >
                        Dosya Seç
                      </Button>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setEffectUploadFormOpen(false)}
                    disabled={isUploading}
                  >
                    İptal
                  </Button>
                  <Button
                    onClick={async () => {
                      if (!effectUploadData.title || !effectUploadData.file) {
                        toast({
                          title: "Eksik bilgi",
                          description: "Lütfen başlık ve dosya seçin.",
                          variant: "destructive"
                        });
                        return;
                      }

                      if (!effectUploadData.isIntro && !effectUploadData.isOutro) {
                        toast({
                          title: "Kullanım alanı seçin",
                          description: "En az bir kullanım alanı seçmelisiniz.",
                          variant: "destructive"
                        });
                        return;
                      }

                      setIsUploading(true);

                      const categories: string[] = [];
                      if (effectUploadData.isIntro) categories.push("intro_jingle");
                      if (effectUploadData.isOutro) categories.push("outro_jingle");

                      try {
                        for (const category of categories) {
                          const formData = new FormData();
                          formData.append("title", effectUploadData.title);
                          formData.append("category", category);
                          formData.append("file", effectUploadData.file);
                          await axios.post("/ai-announcements/music", formData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                        }

                        queryClient.invalidateQueries({ queryKey: ["music-tracks"] });
                        setEffectUploadFormOpen(false);
                        setEffectUploadData({ title: "", isIntro: true, isOutro: false, file: null });
                        toast({
                          title: "Başarılı",
                          description: `Efekt ${categories.length > 1 ? 'her iki kategori için' : ''} başarıyla yüklendi.`
                        });
                      } catch (error) {
                        toast({
                          title: "Hata",
                          description: "Efekt yüklenirken bir hata oluştu.",
                          variant: "destructive"
                        });
                      } finally {
                        setIsUploading(false);
                      }
                    }}
                    disabled={isUploading}
                  >
                    {isUploading ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Yükleniyor...
                      </>
                    ) : (
                      'Yükle'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Efekt Kütüphanesi</CardTitle>
              <CardDescription>
                Anons başı ve sonu için kullanılabilir jingle ve efektler
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : (
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Efekt Adı</TableHead>
                        <TableHead>Tür</TableHead>
                        <TableHead>Süre</TableHead>
                        <TableHead>Önizleme</TableHead>
                        <TableHead className="text-right">İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredEffects.length > 0 ? (
                        filteredEffects.map((track: MusicTrack) => (
                          <TableRow key={track._id || track.id}>
                            <TableCell className="font-medium">{track.name || track.originalName || 'İsimsiz Efekt'}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                track.category === 'intro_jingle'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-blue-100 text-blue-800'
                              }`}>
                                {track.category === 'intro_jingle' ? 'Giriş Jingle' : 'Çıkış Jingle'}
                              </span>
                            </TableCell>
                            <TableCell>
                              {track.duration
                                ? `${Math.floor(track.duration / 60)}:${(track.duration % 60).toString().padStart(2, '0')}`
                                : '0:00'}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => togglePlayTrack(track._id || track.id || '', track.cdnUrl || track.url)}
                                disabled={!track.cdnUrl && !track.url}
                              >
                                {playingTrack === (track._id || track.id) ? (
                                  <>
                                    <Pause className="h-4 w-4 mr-1" />
                                    Durdur
                                  </>
                                ) : (
                                  <>
                                    <Play className="h-4 w-4 mr-1" />
                                    Dinle
                                  </>
                                )}
                              </Button>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const trackId = track._id || track.id;
                                  if (trackId) {
                                    deleteTrack.mutate(trackId);
                                  }
                                }}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-4">
                            {effectSearchQuery ? (
                              <>
                                <p>Arama sonucunda efekt bulunamadı</p>
                                <Button
                                  variant="link"
                                  onClick={() => setEffectSearchQuery("")}
                                >
                                  Filtreyi temizle
                                </Button>
                              </>
                            ) : (
                              <p>Henüz efekt kütüphanesinde jingle bulunmuyor</p>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audio-settings">
          <Card>
            <CardHeader>
              <CardTitle>Varsayılan Ses Ayarları</CardTitle>
              <CardDescription>
                Müzik karıştırma ve ses düzeyi için varsayılan ayarları yapılandırın
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label htmlFor="volume">Varsayılan Ses Seviyesi (%)</Label>
                  <span className="text-sm">22%</span>
                </div>
                <Slider
                  id="volume"
                  defaultValue={[22]}
                  max={100}
                  step={1}
                />
                <p className="text-sm text-muted-foreground">
                  Anons ve müzik karıştırıldığında müzik için kullanılacak ses seviyesi
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label htmlFor="fadeIn">Fade In Süresi (saniye)</Label>
                  <span className="text-sm">0.5</span>
                </div>
                <Slider
                  id="fadeIn"
                  defaultValue={[0.5]}
                  min={0}
                  max={2}
                  step={0.1}
                />
                <p className="text-sm text-muted-foreground">
                  Müzik başlangıcında uygulanacak yumuşak geçiş süresi
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label htmlFor="fadeOut">Fade Out Süresi (saniye)</Label>
                  <span className="text-sm">0.7</span>
                </div>
                <Slider
                  id="fadeOut"
                  defaultValue={[0.7]}
                  min={0}
                  max={2}
                  step={0.1}
                />
                <p className="text-sm text-muted-foreground">
                  Müzik bitiminde uygulanacak yumuşak geçiş süresi
                </p>
              </div>
            </CardContent>
            <CardFooter>
              <Button>
                <Save className="w-4 h-4 mr-2" />
                Ayarları Kaydet
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MusicCollectionManager;
